import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sun, Moon, AlertTriangle, Activity, Users, Wallet, Percent, Layers, Building2,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, LabelList,
} from "recharts";
import { getVisaoExecutivaGeral } from "../../api";
import SideBarNavGestaoOrcamentaria from "../../componets/SideBarNavGestaoOrcamentaria";
import { MESES_ABREV, formatCurrency, formatPercent } from "../../utils/orcamento";

const MESES_1_12 = Array.from({ length: 12 }, (_, idx) => idx + 1);
const ANO_ATUAL = new Date().getFullYear();
const OPCOES_ANO = Array.from({ length: ANO_ATUAL + 2 - 2025 + 1 }, (_, i) => 2025 + i);

// Cor por regime — mesma paleta em todo o módulo, nunca ciclada por filtro.
const CORES_REGIME = { CLT: "#7C3AED", PJ: "#0EA5E9", Estagio: "#F59E0B" };

const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  success: "#10B981",
  warning: "#F59E0B",
  danger: "#EF4444",
});

const headerBtnStyle = {
  display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)",
  border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 12px",
  cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
};

function fmtCompact(v) {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `R$ ${(n / 1_000).toFixed(1)}K`;
  return formatCurrency(n);
}

function truncar(texto, max = 26) {
  if (!texto || texto.length <= max) return texto;
  return `${texto.slice(0, max - 1)}…`;
}

function KpiCard({ C, isLight, icon: Icon, cor, label, valor, subtitulo }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px 16px",
      display: "flex", flexDirection: "column", gap: 6,
      boxShadow: isLight ? "0 1px 4px rgba(0,0,0,0.06)" : "0 1px 4px rgba(0,0,0,0.3)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
        <div style={{ width: 26, height: 26, borderRadius: 7, background: `${cor}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon size={13} color={cor} />
        </div>
        <span style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</span>
      </div>
      <div style={{ fontSize: 19, fontWeight: 800, color: C.text }}>{valor}</div>
      {subtitulo && <div style={{ fontSize: 10.5, color: C.muted }}>{subtitulo}</div>}
    </div>
  );
}

function ChartCard({ C, isLight, titulo, subtitulo, height, children }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20,
      boxShadow: isLight ? "0 1px 4px rgba(0,0,0,0.06)" : "0 1px 4px rgba(0,0,0,0.3)",
    }}>
      <span style={{ fontWeight: 700, fontSize: 13, color: C.text, display: "block", marginBottom: 2 }}>
        {titulo}
      </span>
      <span style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 16 }}>
        {subtitulo}
      </span>
      <div style={{ width: "100%", height }}>
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function VisaoExecutivaGeralView({ tituloModulo, departamentos }) {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const navigate = useNavigate();

  const [ano, setAno] = useState(ANO_ATUAL+1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [departamentoSelecionado, setDepartamentoSelecionado] = useState(null);
  const [mesSelecionado, setMesSelecionado] = useState(() => new Date().getMonth() + 1);

  // "Anual" é só uma opção visual a mais no seletor de mês — reaproveita o índice de
  // Dezembro (11) em todo lugar que hoje indexa `..._por_mes[mesSelecionado - 1]`.
  const indiceMes = mesSelecionado === "anual" ? 11 : mesSelecionado - 1;
  const labelMes = mesSelecionado === "anual" ? "Anual" : MESES_ABREV[indiceMes];
  const refMes = `${labelMes}/${ano}`;

  useEffect(() => {
    let cancelado = false;
    async function carregar() {
      setLoading(true);
      setErro("");
      try {
        const resposta = await getVisaoExecutivaGeral(ano);
        if (!cancelado) setData(resposta);
      } catch (e) {
        if (!cancelado) setErro(e.message);
      } finally {
        if (!cancelado) setLoading(false);
      }
    }
    carregar();
    return () => { cancelado = true; };
  }, [ano]);

  // Recorta o payload (todos os 13 departamentos) para só os que esta tela exibe, ranqueados
  // por custo total do ano — mesmo critério de ordenação de Análise por Departamento.
  const departamentosFiltrados = useMemo(() => {
    return (data?.departamentos || [])
      .filter((d) => departamentos.includes(d.departamento))
      .sort((a, b) => b.total_geral - a.total_geral);
  }, [data, departamentos]);

  // Auto-seleciona o departamento de maior custo assim que os dados chegam, sem sobrescrever
  // uma escolha que o usuário já tenha feito (mesmo padrão da Visão Executiva por projeto).
  useEffect(() => {
    if (departamentoSelecionado === null && departamentosFiltrados.length > 0) {
      setDepartamentoSelecionado(departamentosFiltrados[0].departamento);
    }
  }, [departamentosFiltrados, departamentoSelecionado]);

  const deptAtual = useMemo(
    () => departamentosFiltrados.find((d) => d.departamento === departamentoSelecionado) || departamentosFiltrados[0] || null,
    [departamentosFiltrados, departamentoSelecionado]
  );

  const indicadoresMaoDeObra = deptAtual?.indicadores_mao_de_obra || null;

  const headcountEvolucao = useMemo(() => {
    if (!indicadoresMaoDeObra) return [];
    return MESES_1_12.map((mes) => ({ mes: MESES_ABREV[mes - 1], pessoas: indicadoresMaoDeObra.headcount_por_mes[mes - 1] }));
  }, [indicadoresMaoDeObra]);

  const headcountMes = indicadoresMaoDeObra?.headcount_por_mes?.[indiceMes] ?? 0;
  const salarioMedioMes = indicadoresMaoDeObra?.salario_medio_por_mes?.[indiceMes] ?? 0;

  const concentracaoRegime = useMemo(() => {
    const regimeMes = indicadoresMaoDeObra?.regime_por_mes?.[indiceMes] || { CLT: 0, PJ: 0, Estagio: 0 };
    const total = (regimeMes.CLT || 0) + (regimeMes.PJ || 0) + (regimeMes.Estagio || 0);
    if (!total) return [];
    return [
      { regime: "CLT", qtde: regimeMes.CLT || 0, pct: ((regimeMes.CLT || 0) / total) * 100 },
      { regime: "PJ", qtde: regimeMes.PJ || 0, pct: ((regimeMes.PJ || 0) / total) * 100 },
      { regime: "Estágio", qtde: regimeMes.Estagio || 0, pct: ((regimeMes.Estagio || 0) / total) * 100 },
    ].filter((r) => r.qtde > 0);
  }, [indicadoresMaoDeObra, indiceMes]);

  const concentracaoLabel = concentracaoRegime.map((r) => `${r.pct.toFixed(0)}% ${r.regime}`).join(" · ") || "Sem colaboradores no mês";

  const buckets = useMemo(() => {
    if (!deptAtual) return [];
    return (deptAtual.buckets || []).map((b) => ({
      ...b,
      labelCurto: truncar(b.label, 30),
      variacaoPct: b.total_ano_anterior > 0 ? ((b.total - b.total_ano_anterior) / b.total_ano_anterior) * 100 : null,
    }));
  }, [deptAtual]);

  const concentracaoGastos = useMemo(() => [...buckets].sort((a, b) => b.total - a.total), [buckets]);

  const tooltipStyle = { backgroundColor: C.card, borderColor: C.border, borderRadius: 8, color: C.text, fontSize: 12 };
  const semDados = !loading && departamentosFiltrados.length === 0;
  const mostrarSeletorDepartamento = departamentosFiltrados.length > 1;

  const reajusteMedioPct = indicadoresMaoDeObra?.reajuste_medio_pct;
  const qtdeReajustes = indicadoresMaoDeObra?.reajustes?.length || 0;

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavGestaoOrcamentaria isLight={isLight} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)", color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· {tituloModulo}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <select value={ano} onChange={(e) => setAno(parseInt(e.target.value, 10))}
              style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 10px", color: "#fff", fontSize: 13, fontWeight: 600 }}>
              {OPCOES_ANO.map((a) => <option key={a} value={a} style={{ color: "#0F172A" }}>{a}</option>)}
            </select>
            <button onClick={() => setIsLight((l) => !l)} style={headerBtnStyle}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />} {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button onClick={() => navigate("/inicio")} style={headerBtnStyle}>Menu Principal</button>
          </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 20 }}>
          {erro && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: `${C.danger}18`, border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 10, padding: "10px 14px", fontSize: 13 }}>
              <AlertTriangle size={16} /> {erro}
            </div>
          )}

          <div>
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text, display: "flex", alignItems: "center", gap: 8 }}>
              <Activity size={16} color={C.brand} /> {tituloModulo} · {ano}
            </h2>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: C.muted }}>
              Indicadores de mão de obra e concentração de gastos por categoria — {deptAtual ? deptAtual.departamento : "carregando…"}.
            </p>
          </div>

          {loading ? (
            <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13 }}>Carregando…</div>
          ) : semDados ? (
            <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14 }}>
              Nenhum departamento disponível para {ano}.
            </div>
          ) : (
            <>
              {mostrarSeletorDepartamento && (
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                    <Building2 size={15} color={C.brand} />
                    <span style={{ fontWeight: 700, fontSize: 13, color: C.text }}>Ranking de departamentos</span>
                    <span style={{ fontSize: 11, color: C.muted }}>— ordenado por custo total em {ano}. Clique numa linha para ver o detalhe abaixo.</span>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: "left", padding: "6px 10px", color: C.muted, fontWeight: 700, borderBottom: `1px solid ${C.border}` }}>Departamento</th>
                          <th style={{ textAlign: "right", padding: "6px 10px", color: C.muted, fontWeight: 700, borderBottom: `1px solid ${C.border}` }}>Pessoas ({labelMes})</th>
                          <th style={{ textAlign: "right", padding: "6px 10px", color: C.muted, fontWeight: 700, borderBottom: `1px solid ${C.border}` }}>Custo total ({ano})</th>
                        </tr>
                      </thead>
                      <tbody>
                        {departamentosFiltrados.map((d) => {
                          const selecionado = d.departamento === departamentoSelecionado;
                          return (
                            <tr
                              key={d.departamento} onClick={() => setDepartamentoSelecionado(d.departamento)}
                              style={{ cursor: "pointer", background: selecionado ? `${C.brand}14` : "transparent" }}
                              onMouseEnter={(e) => { if (!selecionado) e.currentTarget.style.background = C.bg; }}
                              onMouseLeave={(e) => { if (!selecionado) e.currentTarget.style.background = "transparent"; }}
                            >
                              <td style={{ padding: "8px 10px", fontWeight: selecionado ? 700 : 600, color: selecionado ? C.brand : C.text, borderBottom: `1px solid ${C.border}` }}>{d.departamento}</td>
                              <td style={{ padding: "8px 10px", textAlign: "right", color: C.text, borderBottom: `1px solid ${C.border}` }}>{d.indicadores_mao_de_obra.headcount_por_mes[indiceMes]}</td>
                              <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: C.text, borderBottom: `1px solid ${C.border}` }}>{fmtCompact(d.total_geral)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text, display: "flex", alignItems: "center", gap: 8 }}>
                  <Users size={16} color={C.brand} /> Indicadores de Mão de Obra
                </h2>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase" }}>Filtro do Período</span>
                  <select
                    value={mesSelecionado}
                    onChange={(e) => {
                      const v = e.target.value;
                      setMesSelecionado(v === "anual" ? "anual" : Number(v));
                    }}
                    style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 12, padding: "6px 10px", fontFamily: "inherit" }}
                  >
                    {MESES_ABREV.map((m, idx) => <option key={m} value={idx + 1}>{m}</option>)}
                    <option value="anual">Anual</option>
                  </select>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
                <KpiCard C={C} isLight={isLight} icon={Users} cor={C.brand} label="Nº total de pessoas"
                  valor={headcountMes} subtitulo={`em ${refMes} — evolução no gráfico abaixo`} />
                <KpiCard C={C} isLight={isLight} icon={Layers} cor={C.brand} label="Concentração PJ/CLT"
                  valor={concentracaoLabel} subtitulo={`${headcountMes} pessoa${headcountMes === 1 ? "" : "s"} em ${refMes}`} />
                <KpiCard C={C} isLight={isLight} icon={Wallet} cor={C.brand} label="Média Salarial"
                  valor={formatCurrency(salarioMedioMes)} subtitulo={`salário-base médio em ${refMes}`} />
                <KpiCard C={C} isLight={isLight} icon={Percent} cor={reajusteMedioPct == null ? C.muted : C.success} label="% Médio de Reajuste no Ano"
                  valor={reajusteMedioPct == null ? "—" : formatPercent(reajusteMedioPct)}
                  subtitulo={qtdeReajustes > 0 ? `média entre ${qtdeReajustes} reajuste${qtdeReajustes === 1 ? "" : "s"} em ${ano}` : `sem reajustes registrados em ${ano}`} />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 14 }}>
                <ChartCard C={C} isLight={isLight} height={260} titulo="Nº total de pessoas — evolução mensal"
                  subtitulo={`Headcount (inclui vagas em aberto) mês a mês em ${ano} — ${deptAtual.departamento}`}>
                  <LineChart data={headcountEvolucao} margin={{ top: 8, right: 20, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                    <XAxis dataKey="mes" stroke={C.muted} fontSize={12} tickLine={false} axisLine={false} dy={6} />
                    <YAxis stroke={C.muted} fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} width={30} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} pessoa${v === 1 ? "" : "s"}`, "Headcount"]} />
                    <Line type="monotone" dataKey="pessoas" name="Headcount" stroke={C.brand} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ChartCard>

                <ChartCard C={C} isLight={isLight} height={Math.max(120, concentracaoRegime.length * 50)} titulo="Concentração PJ/CLT"
                  subtitulo={`Regime de contratação em ${refMes}`}>
                  {concentracaoRegime.length === 0 ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: C.muted, fontSize: 12 }}>
                      Nenhum colaborador ativo neste mês.
                    </div>
                  ) : (
                    <BarChart data={concentracaoRegime} layout="vertical" margin={{ top: 4, right: 50, bottom: 4, left: 4 }} barCategoryGap="30%">
                      <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
                      <XAxis type="number" domain={[0, 100]} stroke={C.muted} fontSize={10} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} />
                      <YAxis dataKey="regime" type="category" stroke={C.muted} fontSize={11} tickLine={false} axisLine={false} width={60} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v, _n, item) => [`${v.toFixed(1)}% (${item.payload.qtde} pessoa${item.payload.qtde === 1 ? "" : "s"})`, item.payload.regime]} />
                      <Bar dataKey="pct" name="Concentração" radius={[0, 4, 4, 0]} maxBarSize={26}>
                        {concentracaoRegime.map((r) => <Cell key={r.regime} fill={CORES_REGIME[r.regime === "Estágio" ? "Estagio" : r.regime]} />)}
                        <LabelList dataKey="pct" position="right" fontSize={10} fill={C.muted} formatter={(v) => `${v.toFixed(0)}%`} />
                      </Bar>
                    </BarChart>
                  )}
                </ChartCard>
              </div>

              <div>
                <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text, display: "flex", alignItems: "center", gap: 8 }}>
                  <Layers size={16} color={C.brand} /> Indicadores Gerais
                </h2>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: C.muted }}>
                  Concentração de gastos por categoria mãe (5 categorias gerais + Mão de Obra) e comparativo com {ano - 1}.
                </p>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 14 }}>
                <ChartCard C={C} isLight={isLight} height={Math.max(240, concentracaoGastos.length * 40)}
                  titulo="Concentração de gastos" subtitulo={`Total planejado em ${ano} por categoria mãe — ${deptAtual.departamento}`}>
                  {concentracaoGastos.every((b) => b.total === 0) ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: C.muted, fontSize: 12 }}>
                      Nenhum valor planejado em {ano}.
                    </div>
                  ) : (
                    <BarChart data={concentracaoGastos} layout="vertical" margin={{ top: 4, right: 70, bottom: 4, left: 4 }} barCategoryGap="24%">
                      <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
                      <XAxis type="number" stroke={C.muted} fontSize={10} tickLine={false} axisLine={false} tickFormatter={fmtCompact} />
                      <YAxis dataKey="labelCurto" type="category" stroke={C.muted} fontSize={11} tickLine={false} axisLine={false} width={170} />
                      <Tooltip
                        contentStyle={tooltipStyle}
                        formatter={(v, _n, item) => [`${formatCurrency(v)} (${item.payload.pct.toFixed(1)}%)`, "Total"]}
                        labelFormatter={(_, payload) => payload?.[0]?.payload?.label || ""}
                      />
                      <Bar dataKey="total" name="Total" fill={C.brand} radius={[0, 4, 4, 0]} maxBarSize={22}>
                        <LabelList dataKey="pct" position="right" fontSize={10} fill={C.muted} formatter={(v) => `${v.toFixed(1)}%`} />
                      </Bar>
                    </BarChart>
                  )}
                </ChartCard>

                <ChartCard C={C} isLight={isLight} height={Math.max(260, buckets.length * 46)}
                  titulo="Comparativo com o ano anterior" subtitulo={`Total planejado por categoria mãe — ${ano} vs ${ano - 1}`}>
                  <BarChart data={buckets} layout="vertical" margin={{ top: 4, right: 20, bottom: 4, left: 4 }} barCategoryGap="24%" barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
                    <XAxis type="number" stroke={C.muted} fontSize={10} tickLine={false} axisLine={false} tickFormatter={fmtCompact} />
                    <YAxis dataKey="labelCurto" type="category" stroke={C.muted} fontSize={11} tickLine={false} axisLine={false} width={170} />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(v, name) => [formatCurrency(v), name]}
                      labelFormatter={(_, payload) => payload?.[0]?.payload?.label || ""}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="total_ano_anterior" name={`${ano - 1}`} fill={C.muted} radius={[0, 4, 4, 0]} maxBarSize={16} />
                    <Bar dataKey="total" name={`${ano}`} fill={C.brand} radius={[0, 4, 4, 0]} maxBarSize={16} />
                  </BarChart>
                </ChartCard>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
